import type {
  DelegationBlockedReason,
  ExecutorItemRef,
  PullRequestChecksStatus,
  PullRequestReviewDecision,
  PullRequestState,
} from './doboxExecutor';

export enum AgentDelegationKind {
  LOCAL_MCP_CODING = 'LOCAL_MCP_CODING',
  AGENT_RUN = 'AGENT_RUN',
}

export enum AgentDelegationStatus {
  QUEUED = 'QUEUED',
  // compareAndSetAgentDelegation's claim step, before the MCP call that mints jobId —
  // see DISPATCH_UNCONFIRMED below for the only way a stuck one is ever reclaimed.
  DISPATCHING = 'DISPATCHING',
  RUNNING = 'RUNNING',
  BLOCKED = 'BLOCKED',
  PLAN_READY = 'PLAN_READY',
  PAUSED = 'PAUSED',
  // The target device was offline at dispatch time; valid for 72h (DEVICE_OFFLINE_EXPIRED
  // past that), re-checked for staleness when the device comes back online.
  WAITING_FOR_DEVICE = 'WAITING_FOR_DEVICE',
  DONE = 'DONE',
  FAILED = 'FAILED',
}

export enum AgentDelegationTaskType {
  TECHNICAL_ANALYSIS = 'TECHNICAL_ANALYSIS',
  IMPLEMENT_AND_PR = 'IMPLEMENT_AND_PR',
  // Driven by the DoBox executor's delegate_code_task tool rather than the legacy Delegate
  // menu — status/progress read from the ledger via ledgerConversationId/ledgerRef, not the
  // executionLog below.
  EXECUTE = 'EXECUTE',
}

export enum LocalCodingAgentRuntime {
  CLAUDE = 'claude',
  CODEX = 'codex',
  AIDER = 'aider',
}

export enum AgentDelegationAvailability {
  AVAILABLE = 'AVAILABLE',
  BUSY = 'BUSY',
  OFFLINE = 'OFFLINE',
}

/** Known causes behind a FAILED (or turns/session-interrupted) delegation, used to key a static
 *  guidance map. UNKNOWN means classifyDelegationError found no match — callers fall back to
 *  a generic "ask the assistant" path. */
export enum AgentDelegationErrorCode {
  BRANCH_EXISTS = 'BRANCH_EXISTS',
  NEEDS_REPO_ACCESS = 'NEEDS_REPO_ACCESS',
  TIMEOUT = 'TIMEOUT',
  MAX_TURNS = 'MAX_TURNS',
  NO_SESSION_TO_RESUME = 'NO_SESSION_TO_RESUME',
  LOCAL_AGENT_UNREACHABLE = 'LOCAL_AGENT_UNREACHABLE',
  PERMISSION_POLICY_BLOCKED = 'PERMISSION_POLICY_BLOCKED',
  /** The code changed and pushed fine; only the runner's follow-up PR-description edit failed. */
  PR_DESCRIPTION_UPDATE_FAILED = 'PR_DESCRIPTION_UPDATE_FAILED',
  /** The agent finished without producing any change, and gave no reason the runner could use. */
  NO_CHANGES = 'NO_CHANGES',
  /** A resolve-comments job whose PR genuinely has no open review comments and no instructions
   *  of its own — the runner never ran the agent, so this is distinct from NO_CHANGES. */
  NO_REVIEW_COMMENTS = 'NO_REVIEW_COMMENTS',
  /** A custom agent's run ended without attempting any step of a job that had not been done yet. */
  NO_STEP_ATTEMPTED = 'NO_STEP_ATTEMPTED',
  /** The McpConnection or LocalDevice this delegation dispatched through was disconnected or
   *  removed between dispatch and the next poll. */
  CONNECTION_NOT_FOUND = 'CONNECTION_NOT_FOUND',
  /** The LocalCodingJob row this delegation's jobId points to is gone — a stale pointer,
   *  distinct from the connection itself disappearing. */
  LOCAL_JOB_NOT_FOUND = 'LOCAL_JOB_NOT_FOUND',
  /** A DISPATCHING delegation sat past its claim window with no idempotencyKey echoed back
   *  by the daemon, so it was never blindly re-dispatched — see AgentDelegationStatus.DISPATCHING. */
  DISPATCH_UNCONFIRMED = 'DISPATCH_UNCONFIRMED',
  /** The device stayed offline through the whole WAITING_FOR_DEVICE window (72h). */
  DEVICE_OFFLINE_EXPIRED = 'DEVICE_OFFLINE_EXPIRED',
  /** The source content changed after a plan was approved but before dispatch confirmed —
   *  approveInboxEntryDelegationPlan's planHash mismatch, surfaced as CONFLICT rather than
   *  dispatching against a plan the user never saw. */
  SOURCE_CHANGED_BEFORE_DISPATCH = 'SOURCE_CHANGED_BEFORE_DISPATCH',
  UNKNOWN = 'UNKNOWN',
}

/** Whether the delegation's underlying DoBox originated inside the user's own account
 *  (TYPED/ANSWER on their own thread) or from an external party — gates whether the local
 *  daemon's verification command is allowed to run against agent-written code at all. */
export enum DelegationSourceOrigin {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL',
}

/** The kind of work a running delegation is doing, derived from the local job's log.
 *  Rendered through a translation key at the presentation layer — never displayed raw. */
export enum AgentDelegationActivity {
  READING = 'READING',
  SEARCHING = 'SEARCHING',
  EDITING = 'EDITING',
  RUNNING_COMMANDS = 'RUNNING_COMMANDS',
  DELEGATING = 'DELEGATING',
  RESEARCHING = 'RESEARCHING',
  PLANNING = 'PLANNING',
  WORKING = 'WORKING',
}

/** Why a DONE delegation ended other than by finishing its work. The status stays DONE;
 *  an absent value means it finished normally. */
export enum AgentDelegationEndedReason {
  DECLINED_BY_OWNER = 'DECLINED_BY_OWNER',
}

export enum AgentDelegationEventKind {
  DELEGATED = 'DELEGATED',
  STATUS_CHANGE = 'STATUS_CHANGE',
  QUESTION_ASKED = 'QUESTION_ASKED',
  ANSWERED = 'ANSWERED',
  FAILED = 'FAILED',
  RETRIED = 'RETRIED',
  PLAN_READY = 'PLAN_READY',
  PLAN_REFINED = 'PLAN_REFINED',
  PR_UPDATE_REQUESTED = 'PR_UPDATE_REQUESTED',
  COMPLETED = 'COMPLETED',
}

/** One entry in a delegation's capped executionLog — the "history" a user can read to understand
 *  how the ticket got to its current state across retries/answers/failures. */
export interface AgentDelegationEvent {
  at: number;
  kind: AgentDelegationEventKind;
  status: AgentDelegationStatus;
  message?: string;
  attempt?: number;
}

/**
 * Statuses where a delegation is still "in flight" — used to block starting a
 * second delegation on the same ticket and to gate answerInboxEntryDelegation.
 */
export const NON_TERMINAL_AGENT_DELEGATION_STATUSES: ReadonlySet<AgentDelegationStatus> =
  new Set([
    AgentDelegationStatus.QUEUED,
    AgentDelegationStatus.DISPATCHING,
    AgentDelegationStatus.RUNNING,
    AgentDelegationStatus.BLOCKED,
    AgentDelegationStatus.PLAN_READY,
    AgentDelegationStatus.PAUSED,
    AgentDelegationStatus.WAITING_FOR_DEVICE,
  ]);

export interface AgentDelegationPendingQuestion {
  text: string;
  options?: string[];
  askedAt: number;
  isApproval?: boolean;
}

/** What a direct-send action will actually do, read from its toolInput — the label is
 *  model-written and can misdescribe it. Capped; sealed at rest with the rest of the
 *  delegation content. */
export interface AgentDelegationActionEffect {
  recipients?: string[];
  /** Total before `recipients` was capped. */
  recipientCount?: number;
  target?: string;
  subject?: string;
  bodyPreview?: string;
  /** File or media names the action uploads or publishes, capped. */
  attachmentNames?: string[] | null;
  /** Total before `attachmentNames` was capped. */
  attachmentCount?: number | null;
  /** The workspace, page or account the action runs against. */
  workspace?: string | null;
}

/** The raw toolInput is never persisted. toolName/effect are set only for a tool in
 *  DIRECT_SEND_GATED_TOOL_NAMES. */
export interface AgentDelegationProposedAction {
  id: string;
  label: string;
  toolName?: string;
  effect?: AgentDelegationActionEffect;
}

export interface InboxEntryAgentDelegation {
  /** Identifies one delegation lifecycle (delegate until replaced or ended). Minted by every
   *  fresh-template builder; in-place writers spread it through. Absent on legacy delegations. */
  delegationId?: string;
  kind: AgentDelegationKind;
  status: AgentDelegationStatus;
  taskType: AgentDelegationTaskType;
  localRuntime?: LocalCodingAgentRuntime;
  localModel?: string;
  localAgentProfile?: string;
  /** Clerk user id of whoever created this delegation — lets the FE scope agent
   *  notifications to the delegator. Optional: legacy delegations predate this field. */
  delegatedByUserId?: string;
  targetName: string;
  mcpConnectionId?: string;
  jobId?: string;
  conversationId?: string;
  runId?: string;
  agentTemplateId?: string;
  /** Owner-set for this task: the executor applies every action except sending outside and deleting without a card. Absent means off. */
  skipApprovals?: boolean;
  statusMessage?: string;
  /** What a RUNNING delegation is doing right now. An enum, not copy: the frontend renders it
   *  through a translation key, so the job's raw log line never reaches a user. */
  activity?: AgentDelegationActivity;
  /** Full failure text (capped, see truncateDelegationError) — kept separate from statusMessage
   *  so the FE's single-line status field never has to left-truncate a long raw error and lose
   *  the useful part at the end (see summarizeDelegationError). */
  error?: string;
  errorCode?: AgentDelegationErrorCode;
  /** Short tail of the job's log, only present when a local coding job failed/was interrupted. */
  logTailExcerpt?: string;
  /** Capped history of lifecycle transitions (see appendDelegationEvent) — the "continue where
   *  you left off" narrative surfaced on the card; never reset on retry. */
  executionLog?: AgentDelegationEvent[];
  pendingQuestion?: AgentDelegationPendingQuestion;
  /** actions is always present (possibly empty) once pendingApproval is set. */
  pendingApproval?: {
    message?: string;
    actions: AgentDelegationProposedAction[];
    /** Links this approval back to the executor PROPOSAL ledger row that spawned this
     *  taskType EXECUTE delegation, so confirming it resolves by ref rather than by matching
     *  free text. */
    proposalRef?: {
      runId: string;
      convMsgIdx: string;
    };
  };
  prUrl?: string;
  plan?: string;
  /** Derived, never persisted — recomputed from `plan` on every GraphQL read
   *  (AgentDelegation.planHash). Absent until the plan is opened; WEB-P0.04 sends it back
   *  to approveInboxEntryDelegationPlan so the server can refuse a plan that moved. */
  planHash?: string;
  /** Set when compareAndSetAgentDelegation claims this delegation for dispatch — the only
   *  token a stuck (>2min) DISPATCHING row is ever reclaimed/re-dispatched against, and only
   *  once the daemon echoes it back as its idempotencyKey. */
  dispatchClaimId?: string;
  dispatchedAt?: number;
  /** The LocalDevices row the dispatch targeted. Readable: a key, not content. */
  deviceId?: string;
  prState?: PullRequestState;
  prNumber?: number;
  prChecksStatus?: PullRequestChecksStatus;
  prChecksFailedCount?: number;
  prReviewDecision?: PullRequestReviewDecision;
  prReviewCommentCount?: number;
  /** Epoch ms of the last time any PR signal value changed. */
  prSignalsUpdatedAt?: number;
  /** Keyed HMAC of githubRepository + the PR number in messageUrl, computed by the UCA
   *  localJobWatcher hook — compared by the GitHub-hook reconciler, never decrypted. */
  prKeyHmac?: string;
  repoKeyHmac?: string;
  sourceOrigin?: DelegationSourceOrigin;
  /** The DOBOX_EXECUTOR conversation this delegation reports its status into, once driven by
   *  taskType EXECUTE — status reads from the ledger via ledgerRef, not executionLog. */
  ledgerConversationId?: string;
  ledgerRef?: ExecutorItemRef;
  blockedReason?: DelegationBlockedReason;
  /** Readable by argument: the web branches on it structurally (DONE + this = "declined", not "done"). Set only with status DONE; every writer that starts or re-settles the delegation clears it. */
  endedReason?: AgentDelegationEndedReason;
  /** owner/repo of the dispatch target. SEALED — folded into agentDelegationContentJson by
   *  M-CODE.01 — because a personal owner/repo names a person. */
  repositorySlug?: string;
  startedAt: number;
  finishedAt?: number;
}

export const DEFAULT_ASSISTANT_DELEGATION_TARGET_NAME = 'Assistant';

/** The "Ask assistant about this ticket" chat-link placeholder — a conversation link, not a
 *  run a user delegated. */
export function isDoBoxChatLinkedDelegation(
  delegation: Pick<
    InboxEntryAgentDelegation,
    'kind' | 'targetName' | 'agentTemplateId'
  >,
): boolean {
  return (
    delegation.kind === AgentDelegationKind.AGENT_RUN &&
    delegation.agentTemplateId == null &&
    delegation.targetName === DEFAULT_ASSISTANT_DELEGATION_TARGET_NAME
  );
}

/** False for a chat-link and for no delegation at all. */
export function isUserVisibleDelegation(
  delegation:
    | Pick<InboxEntryAgentDelegation, 'kind' | 'targetName' | 'agentTemplateId'>
    | null
    | undefined,
): boolean {
  return delegation != null && !isDoBoxChatLinkedDelegation(delegation);
}

export interface AgentDelegationAgentProfile {
  name: string;
  description?: string;
}

export interface AgentDelegationTarget {
  id: string;
  kind: AgentDelegationKind;
  name: string;
  subtitle?: string;
  availability: AgentDelegationAvailability;
  activeTaskCount?: number;
  availableLocalRuntimes?: LocalCodingAgentRuntime[];
  availableAgentProfiles?: AgentDelegationAgentProfile[];
}

export const MAX_DELEGATION_EXECUTION_LOG_EVENTS = 20;
export const DELEGATION_ERROR_MAX_LENGTH = 2000;
export const DELEGATION_AGENT_MESSAGE_MAX_LENGTH = 4000;
const DELEGATION_STATUS_SUMMARY_MAX_LENGTH = 160;
const DELEGATION_LOG_TAIL_EXCERPT_MAX_LENGTH = 800;

const BRANCH_EXISTS_PATTERN = /already exists/i;
// Scoped to the delegate's own wording and log markers. Broad patterns like /requires
// approval/ also matched a repository's verification output — up to 4000 chars of lint and
// test text now land in `error`, so a failing test name was enough to tell the user a policy
// blocked them and that retrying would not help, when retrying was the correct action.
const PERMISSION_POLICY_BLOCKED_PATTERNS = [
  /permission mode forced to/i,
  /permission policy blocked the job/i,
  /^\[tool:denied\] /m,
];
const NEEDS_REPO_ACCESS_PATTERNS = [
  /\b404\b/,
  /repo(?:sitory)? access/i,
  /could not read from remote/i,
  /permission denied.*(repository|remote)/i,
];
const TIMEOUT_PATTERNS = [/timed out/i, /\btimeout\b/i];
const NO_SESSION_PATTERNS = [
  /no resumable session/i,
  /could not resume/i,
  /interrupted and could not resume/i,
];
const LOCAL_AGENT_UNREACHABLE_PATTERNS = [
  /unreachable/i,
  /could not reach/i,
  /failed to reach/i,
  /econnrefused/i,
];

/** publishImplementation's FAIL_NO_WORK fallback — shared so the daemon and the classifier
 *  below always agree on the exact wording. */
export const NO_CHANGES_ERROR_MESSAGE =
  'The agent finished without changing any file (see logTail)';

/** The worker's reason for a custom-agent run that finished without attempting any step of its job.
 *  Shared so the settle that writes it and the classifier that maps it to its own error code agree. */
export const NO_STEP_ATTEMPTED_ERROR_MESSAGE =
  'The agent finished without attempting any step of its job.';

/** The worker's reason for a custom-agent run that used every turn it was given before finishing its job. */
export const MAX_TURNS_ERROR_MESSAGE =
  'The agent ran out of steps before finishing its job.';

/** Pure classification of a raw failure string (+ optional log tail for extra context) into one
 *  of the known AgentDelegationErrorCode buckets. UNKNOWN is the safe default — callers fall back
 *  to a generic "ask the assistant" guidance path rather than a wrong specific one. */
export function classifyDelegationError(
  rawError: string,
  logTail?: string,
): AgentDelegationErrorCode {
  // Ahead of everything else, including PERMISSION_POLICY_BLOCKED, and tested against rawError
  // ALONE, never the haystack below: the runner sets rawError to begin with this exact prefix
  // only when this is the genuine cause, which makes it more reliable than any text pattern —
  // and the job log is append-mode, so a stale copy of this same prefix from an earlier attempt
  // must never be allowed to reclassify a later, unrelated failure.
  if (rawError.startsWith(PR_BODY_EDIT_FAILED_LOG_PREFIX)) {
    return AgentDelegationErrorCode.PR_DESCRIPTION_UPDATE_FAILED;
  }
  // Same rawError-only treatment, and ahead of the NO_CHANGES haystack check below: this fires
  // before the agent ever runs, over a job outcome NO_CHANGES's own text could never coincide
  // with, but a marker we control is still the more reliable signal to check first.
  if (rawError.startsWith(NO_REVIEW_COMMENTS_ERROR_PREFIX)) {
    return AgentDelegationErrorCode.NO_REVIEW_COMMENTS;
  }

  if (rawError.startsWith(NO_STEP_ATTEMPTED_ERROR_MESSAGE)) {
    return AgentDelegationErrorCode.NO_STEP_ATTEMPTED;
  }

  if (rawError.startsWith(MAX_TURNS_ERROR_MESSAGE)) {
    return AgentDelegationErrorCode.MAX_TURNS;
  }

  const haystack = `${rawError}\n${logTail ?? ''}`;

  // Ahead of the rest: a policy-blocked run also produces downstream noise (no branch, no
  // push), and classifying it as one of those hides the only cause the user can act on.
  if (
    PERMISSION_POLICY_BLOCKED_PATTERNS.some((pattern) => pattern.test(haystack))
  ) {
    return AgentDelegationErrorCode.PERMISSION_POLICY_BLOCKED;
  }
  if (BRANCH_EXISTS_PATTERN.test(haystack)) {
    return AgentDelegationErrorCode.BRANCH_EXISTS;
  }
  if (NEEDS_REPO_ACCESS_PATTERNS.some((pattern) => pattern.test(haystack))) {
    return AgentDelegationErrorCode.NEEDS_REPO_ACCESS;
  }
  if (TIMEOUT_PATTERNS.some((pattern) => pattern.test(haystack))) {
    return AgentDelegationErrorCode.TIMEOUT;
  }
  if (NO_SESSION_PATTERNS.some((pattern) => pattern.test(haystack))) {
    return AgentDelegationErrorCode.NO_SESSION_TO_RESUME;
  }
  if (
    LOCAL_AGENT_UNREACHABLE_PATTERNS.some((pattern) => pattern.test(haystack))
  ) {
    return AgentDelegationErrorCode.LOCAL_AGENT_UNREACHABLE;
  }
  // After PERMISSION_POLICY_BLOCKED: a policy-blocked run can also end with zero changes, and
  // that specific, actionable cause must not be hidden behind this generic, no-work-happened one.
  if (haystack.includes(NO_CHANGES_ERROR_MESSAGE)) {
    return AgentDelegationErrorCode.NO_CHANGES;
  }
  return AgentDelegationErrorCode.UNKNOWN;
}

/** Appends one event to a delegation's executionLog, discarding the oldest entries beyond
 *  MAX_DELEGATION_EXECUTION_LOG_EVENTS. Never resets the log — retries/answers grow it, they
 *  don't clear it, since the log IS the continuity history the user relies on. */
export function appendDelegationEvent(
  delegation: Pick<InboxEntryAgentDelegation, 'executionLog'>,
  event: AgentDelegationEvent,
): AgentDelegationEvent[] {
  const next = [...(delegation.executionLog ?? []), event];
  return next.length > MAX_DELEGATION_EXECUTION_LOG_EVENTS
    ? next.slice(next.length - MAX_DELEGATION_EXECUTION_LOG_EVENTS)
    : next;
}

const SHELL_WRAPPER_ERROR_PATTERN = /exited with \d+:\s*(.+)$/is;

/**
 * The FE's status field renders as a single truncated line. A raw shell-wrapper error such as
 * jobs.ts's runCommand ("git -C <repoDir> worktree add <path> -b <branch> origin/main exited
 * with 128: fatal: a branch named '<branch>' already exists") puts the useful stderr AFTER a long
 * command invocation — left-truncating the raw string on display cuts it off before the "fatal:"
 * part ever shows, which is the exact bug this fixes. Prefer the stderr tail when the shape
 * matches, otherwise fall back to (and only then length-cap) the raw text.
 */
export function summarizeDelegationError(rawError: string): string {
  const trimmed = rawError.trim();
  const shellWrapperMatch = trimmed.match(SHELL_WRAPPER_ERROR_PATTERN);
  const source = shellWrapperMatch?.[1]?.trim() || trimmed;
  return source.length > DELEGATION_STATUS_SUMMARY_MAX_LENGTH
    ? `${source.slice(0, DELEGATION_STATUS_SUMMARY_MAX_LENGTH)}...`
    : source;
}

/** Caps the full raw error text stored in InboxEntryAgentDelegation.error. */
export function truncateDelegationError(rawError: string): string {
  const trimmed = rawError.trim();
  return trimmed.length > DELEGATION_ERROR_MAX_LENGTH
    ? `${trimmed.slice(0, DELEGATION_ERROR_MAX_LENGTH)}...`
    : trimmed;
}

// Host-agnostic on purpose: a self-hosted GitHub Enterprise instance serves the same
// /<owner>/<repo>/pull/<number> shape from its own domain.
const PULL_REQUEST_URL_PATTERN =
  /^https:\/\/[\w.-]+\/[\w.-]+\/[\w.-]+\/pull\/\d+(?:[/?#].*)?$/;

/** prUrl is agent-reported text. A whole sentence ("NONE (blocked on write permission …)")
 *  has been stored as one and then opened as a link relative to the app origin, so every
 *  surface that stores, links or parses a prUrl gates on this first. */
export function isPullRequestUrl(value?: string): boolean {
  return Boolean(value && PULL_REQUEST_URL_PATTERN.test(value.trim()));
}

/** The local job log's per-step line prefix. Written by the delegate runner's stream handler
 *  and read back by resolveDelegationActivity — the two must not drift apart. */
export const DELEGATION_TOOL_LOG_PREFIX = '[tool] ';

/** Marks the agent's final-message line in the job log — written once per run by the runner's
 *  Claude and Codex stream handlers, read back by the polling UI's log-line filter. */
export const DELEGATION_RESULT_LOG_PREFIX = '[result] ';

/** Marks a failed PR-description edit, both in the job log (appended by the runner after a code
 *  push already succeeded) and at the START of a description-only job's own FAILED error text —
 *  classifyDelegationError keys PR_DESCRIPTION_UPDATE_FAILED off that second occurrence only. */
export const PR_BODY_EDIT_FAILED_LOG_PREFIX = '[pr-body:failed] ';

/** Marks the START of the FAILED error text for a RESOLVE_COMMENTS job the runner refused to
 *  even start: the PR genuinely has no open review comments and no instructions were given.
 *  Never written to the job log — the runner fails before the agent (and its log stream) ever
 *  starts — but kept alongside PR_BODY_EDIT_FAILED_LOG_PREFIX and checked the same rawError-only
 *  way, for the same reliability reason. */
export const NO_REVIEW_COMMENTS_ERROR_PREFIX = '[no-review-comments] ';

const TOOL_LOG_LINE_PATTERN = /^\[tool\]\s+(\S+)/;

const ACTIVITY_BY_TOOL_NAME: ReadonlyMap<string, AgentDelegationActivity> =
  new Map([
    ['Read', AgentDelegationActivity.READING],
    ['NotebookRead', AgentDelegationActivity.READING],
    ['Glob', AgentDelegationActivity.SEARCHING],
    ['Grep', AgentDelegationActivity.SEARCHING],
    ['Edit', AgentDelegationActivity.EDITING],
    ['MultiEdit', AgentDelegationActivity.EDITING],
    ['Write', AgentDelegationActivity.EDITING],
    ['NotebookEdit', AgentDelegationActivity.EDITING],
    ['Bash', AgentDelegationActivity.RUNNING_COMMANDS],
    ['BashOutput', AgentDelegationActivity.RUNNING_COMMANDS],
    ['KillShell', AgentDelegationActivity.RUNNING_COMMANDS],
    ['Task', AgentDelegationActivity.DELEGATING],
    ['Agent', AgentDelegationActivity.DELEGATING],
    ['WebFetch', AgentDelegationActivity.RESEARCHING],
    ['WebSearch', AgentDelegationActivity.RESEARCHING],
    ['TodoWrite', AgentDelegationActivity.PLANNING],
    ['ExitPlanMode', AgentDelegationActivity.PLANNING],
    // Codex reports item types rather than tool names. They share this map so one activity
    // vocabulary covers both runtimes, and the log keeps each runtime's real vocabulary.
    ['command_execution', AgentDelegationActivity.RUNNING_COMMANDS],
    ['file_change', AgentDelegationActivity.EDITING],
    ['web_search', AgentDelegationActivity.RESEARCHING],
    ['mcp_tool_call', AgentDelegationActivity.DELEGATING],
    ['collab_tool_call', AgentDelegationActivity.DELEGATING],
    ['todo_list', AgentDelegationActivity.PLANNING],
    ['reasoning', AgentDelegationActivity.WORKING],
  ]);

/** Maps the most recent step in a job's log tail to the activity shown on the delegation card.
 *  undefined means the job has not reported a step yet — the card shows only its status. */
export function resolveDelegationActivity(
  logTail?: string,
): AgentDelegationActivity | undefined {
  if (!logTail) {
    return;
  }
  const lines = logTail.split('\n');
  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const toolName = TOOL_LOG_LINE_PATTERN.exec(lines[index].trim())?.[1];
    if (toolName) {
      return (
        ACTIVITY_BY_TOOL_NAME.get(toolName) ?? AgentDelegationActivity.WORKING
      );
    }
  }
  return;
}

/** Tail excerpt of a job's log for extra failure context — mirrors the "tail" semantics of the
 *  local job's own logTail (most recent output, not the start). */
export function excerptDelegationLogTail(logTail?: string): string | undefined {
  const trimmed = logTail?.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed.length > DELEGATION_LOG_TAIL_EXCERPT_MAX_LENGTH
    ? trimmed.slice(-DELEGATION_LOG_TAIL_EXCERPT_MAX_LENGTH)
    : trimmed;
}
